import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { SampleTrackingStorage } from '../sample-tracking-storage';
import { combineLatest, merge, of, Subscription, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { ActionButtonOption, StudyGroupInfo, StudyInfo } from './study-group-info';
import { StudyGroup } from '../group-info/study-group';
import { TextHighlightService } from '../service/text-highlight/text-highlight-service';
import { SampleTrackingDialogService } from '../service/dialog-service/sample-tracking-dialog-service';
import { GridApi } from 'ag-grid-community';
import { SampleTrackingActionService } from '../service/sample-tracking-action-service';
import { SampleTrackingResultAction, SampleTrackingDialogResult } from '../service/dialog-service/sample-tracking-result';
import { containsCaseInsensitive } from '../../core/search-functions';

@Component({
    selector: 'study-group-list',
    template: require('./study-group-list.component.html'),
    styles: [require('./study-group-list.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StudyGroupListComponent implements OnDestroy {

    @Input()
    public actionButtonOption: ActionButtonOption;

    public searchTerm = new FormControl('');

    public loadingComplete = false;


    public allStudyGroupCards: StudyGroupInfo[];

    public data: StudyGroup[] = [];

    public createGroupEnabled: boolean;

    public filteredStudyGroupCards: StudyGroupInfo[];

    public searchedWords$: Observable<string>;

    private subscription: Subscription;

    private data$: Observable<StudyGroup[]>;

    private gridApi!: GridApi;

    private isSuccess = false;

    private messageText: string;

    constructor(
        private readonly sampleTrackingStorage: SampleTrackingStorage,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly dialogService: SampleTrackingDialogService,
        private readonly textHighlightService: TextHighlightService,
        private readonly actionService: SampleTrackingActionService
    ) {
        const searchTermFlow = merge(
            of(''),
            this.searchTerm.valueChanges
                .pipe(
                    debounceTime(300),
                    distinctUntilChanged()
                )
        );

        this.data$ = this.sampleTrackingStorage.all;

        this.subscription = combineLatest([
            this.data$,
            searchTermFlow
        ]).pipe(
            map(([studies, term]: [StudyGroup[], string]) =>
                [this.createStudyGroupInfo(studies), term]
            ),
            map(([studyGroupCards, term]: [StudyGroupInfo[], string]) =>
                [this.searchTextHighlight(studyGroupCards, term)]
            ))
            .subscribe(([studyCards]: [StudyGroupInfo[]]) => {
                this.allStudyGroupCards = studyCards;
                this.loadingComplete = true;
                this.changeDetectorRef.markForCheck();
            });
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public async createStudyGroup() {

        const result = await this.showCreateDialog().toPromise();
        if (result.action === SampleTrackingResultAction.create) {
            await this.actionService.createGroup(result.createRequest);
        }
    }

    private showCreateDialog(): Observable<SampleTrackingDialogResult> {
        return this.dialogService.showCreateDialog(this.allStudyGroupCards).pipe(
            switchMap(dialog => dialog.onHide()),
            map(dialogResult => dialogResult || { action: SampleTrackingResultAction.close })
        );
    }

    private createStudyGroupInfo(studyGroups: StudyGroup[]) {
        if (!studyGroups || studyGroups.length === 0) { return []; }
        var studyGroupCards: StudyGroupInfo[] = [];
        for (var studyGroup of studyGroups) {
            const studyGroupInfo = studyGroupCards.filter(t =>
                t.groupName.toLowerCase() === studyGroup.studyGroupName.toLowerCase());
            if (!studyGroupInfo || studyGroupInfo.length === 0) {
                var newStudyGroupInfo = this.getStudyGroupInfo(studyGroup);
                studyGroupCards.push(newStudyGroupInfo);
                continue;
            }
            this.addStudyInfo(studyGroup, studyGroupInfo[0]);
            continue;
        }
        return studyGroupCards;
    }

    private setCreateAndUpdateDates(studyGroup: StudyGroup, studyGroupInfo: StudyGroupInfo) {
        const createDate = studyGroup.createDbDate.split(' ');
        studyGroupInfo.createdDate = createDate[0];
        if (!studyGroup.updateDbDate) { return; }
        const updateDate = studyGroup.updateDbDate.split(' ');
        studyGroupInfo.updatedDate = updateDate[0];
    }

    private getStudyGroupInfo(studyGroup: StudyGroup) {
        var newStudyGroupInfo = new StudyGroupInfo();
        newStudyGroupInfo.groupPk = studyGroup.groupPk;
        newStudyGroupInfo.groupName = studyGroup.studyGroupName;
        newStudyGroupInfo.cardGroupName = studyGroup.studyGroupName;
        newStudyGroupInfo.createdDate = studyGroup.createDbDate;
        this.setCreateAndUpdateDates(studyGroup, newStudyGroupInfo);
        this.addStudyInfo(studyGroup, newStudyGroupInfo);
        return newStudyGroupInfo;
    }

    private addStudyInfo(studyGroup: StudyGroup, studyGroupInfo: StudyGroupInfo) {
        var studyInfo = new StudyInfo();
        studyInfo.protocolPk = studyGroup.protocolFk;
        studyInfo.protocolCode = studyGroup.protocolCode;
        studyInfo.cardProtocolCode = studyGroup.protocolCode;
        studyInfo.sponsorName = studyGroup.sponsorName;
        studyGroupInfo.studyList.push(studyInfo);
        studyGroupInfo.cardStudyList.push(studyInfo);
    }

    
    private searchTextHighlight(studyGroupInfo: StudyGroupInfo[], term: string) {
        if (!term) { return studyGroupInfo; }
        let studyGroupList: StudyGroupInfo[] = [];
        for (let groupInfo of studyGroupInfo) {
            if (containsCaseInsensitive(groupInfo.groupName, term)) {
                studyGroupList.push(groupInfo);
            }
            let protocols = groupInfo.studyList.filter(t => containsCaseInsensitive(t.protocolCode, term));
            if (protocols && protocols.length > 0) {
                groupInfo.cardStudyList = protocols;
                studyGroupList.push(groupInfo);
            } else {
                groupInfo.cardStudyList = groupInfo.studyList;
            }
        }
        return studyGroupList;
    }
}
